import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SiteFooter } from "../components/SiteFooter";
import { toast } from "sonner";
import { ArrowRight, Check, MapPin, Users, Hotel, Briefcase, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";
const HERO_IMG = "https://images.unsplash.com/photo-1616455436127-553454f32623?w=1400&q=80";
const CONCIERGE_DEPOSIT_URL = "https://buy.stripe.com/9B68wR2snc3ieCY2bv28801";

const INCLUDES = [
  { id: "guided", icon: MapPin, title: "Guided Sourcing Experience", desc: "Personal tour through LA\u2019s finest cultivation networks, with KookaMF as your home base." },
  { id: "intros", icon: Users, title: "Cultivator Introductions", desc: "Face-to-face meetings with vetted growers and curators beyond the KookaMF network." },
  { id: "bridge", icon: Briefcase, title: "Your Trusted Bridge", desc: "We broker every introduction and meeting on your behalf." },
  { id: "hotel", icon: Hotel, title: "Hotel & Logistics", desc: "Complete trip coordination from arrival to departure." },
];

const FAQ = [
  { id: "faq-deposit", q: "What happens after I pay the deposit?", a: "We\u2019ll contact you within 48 hours to start planning your trip. This includes cultivator matching, scheduling meetings, and coordinating logistics." },
  { id: "faq-who", q: "Who will I be meeting?", a: "Vetted cultivators, curators, and distribution partners hand-selected based on your sourcing needs and volume requirements." },
  { id: "faq-where", q: "What cities/areas does the trip cover?", a: "Primarily greater Los Angeles \u2014 including cultivation hubs in the surrounding valleys and coastal regions. Custom routes available." },
];

export default function ConciergePage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  const fetchSubmission = useCallback(async () => {
    try { const res = await axios.get(`${API}/submissions/${submissionId}`); setSubmission(res.data); }
    catch { toast.error("Failed to load"); navigate("/"); }
    finally { setLoading(false); }
  }, [submissionId, navigate]);

  useEffect(() => { fetchSubmission(); }, [fetchSubmission]);

  const handleDeposit = () => {
    if (!submission) return;
    const url = new URL(CONCIERGE_DEPOSIT_URL);
    url.searchParams.set("client_reference_id", `${submissionId}_concierge`);
    if (submission.email) url.searchParams.set("prefilled_email", submission.email);
    window.location.href = url.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto" style={{ borderColor: "var(--accent)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  const isDepositPaid = submission?.concierge_deposit_paid;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-primary)" }} data-testid="concierge-page">
      {/* Hero */}
      <div className="relative" style={{ minHeight: "clamp(320px, 55vh, 520px)" }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,50,33,0.5) 0%, rgba(15,50,33,0.85) 70%, var(--bg-primary) 100%)" }} />
        <div className="relative z-10 flex flex-col justify-between h-full px-5 sm:px-8 md:px-12 lg:px-20 py-8 md:py-10" style={{ minHeight: "inherit" }}>
          <a href="/"><img src={LOGO_URL} alt="Green Routes" className="h-9 sm:h-10 md:h-12 self-start logo-on-dark" /></a>
          <div className="max-w-2xl">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-3 font-semibold" style={{ color: "rgba(253,252,247,0.4)" }}>Concierge Package</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none mb-4" style={{ color: "#F5F3E9", fontFamily: "'Playfair Display', serif" }}>
              Your LA Sourcing<br />Experience
            </h1>
            <p className="text-sm sm:text-base max-w-md" style={{ color: "rgba(253,252,247,0.65)" }}>
              Private introductions. Vetted relationships. One trusted guide through Los Angeles.
            </p>
          </div>
        </div>
      </div>

      {/* What's Included */}
      <div className="px-5 sm:px-8 md:px-12 lg:px-20 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase text-center mb-10 font-semibold" style={{ color: "#C2693D" }}>What You Get</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {INCLUDES.map((item) => (
              <div key={item.id} className="p-6 md:p-8" style={{ background: "var(--bg-elevated, #fff)", border: "1px solid var(--border)" }}>
                <item.icon size={22} strokeWidth={1.5} style={{ color: "#C2693D" }} className="mb-4" />
                <h3 className="text-base font-medium mb-2" style={{ color: "var(--text-primary)", fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                <p className="text-sm" style={{ color: "var(--text-secondary)", lineHeight: "1.6" }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="px-5 sm:px-8 md:px-12 lg:px-20 pb-12 md:pb-16">
        <div className="max-w-lg mx-auto">
          <div style={{ border: "1px solid var(--border)", overflow: "hidden" }}>
            <div className="p-6 md:p-8 text-center" style={{ background: "var(--primary)" }}>
              <p className="text-xs tracking-[0.2em] uppercase mb-4 font-semibold" style={{ color: "rgba(253,252,247,0.4)" }}>Package Pricing</p>
              <p className="text-4xl md:text-5xl font-medium" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F3E9" }}>$2,000</p>
              <p className="text-sm mt-2" style={{ color: "rgba(253,252,247,0.5)" }}>Total experience</p>
              <div className="flex justify-center gap-8 mt-6 text-sm" style={{ color: "rgba(253,252,247,0.65)" }}>
                <div><span style={{ color: "#C2693D", fontWeight: 600 }}>$500</span> to reserve</div>
                <div><span style={{ color: "#F5F3E9", fontWeight: 600 }}>$1,500</span> before departure</div>
              </div>
            </div>
            <div className="p-6 md:p-8" style={{ background: "var(--bg-elevated, #fff)" }}>
              {isDepositPaid ? (
                <div className="text-center py-2" data-testid="deposit-confirmed">
                  <Check size={28} className="mx-auto mb-2" style={{ color: "var(--success)" }} />
                  <p className="font-medium text-lg" style={{ color: "var(--success)" }}>Deposit Received</p>
                  <p className="text-xs mt-1" style={{ color: "var(--text-tertiary)" }}>We will contact you within 48 hours to plan your trip.</p>
                </div>
              ) : (
                <button onClick={handleDeposit} data-testid="btn-deposit"
                  className="w-full py-4 text-sm font-semibold tracking-wide flex items-center justify-center gap-2"
                  style={{ background: "var(--primary)", color: "#F5F3E9", transition: "background-color 0.2s ease" }}>
                  Reserve Your Experience — $500 <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="px-5 sm:px-8 md:px-12 lg:px-20 pb-12 md:pb-16">
        <div className="max-w-lg mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase text-center mb-8 font-semibold" style={{ color: "#C2693D" }}>FAQ</p>
          {FAQ.map((item) => (
            <div key={item.id} style={{ borderTop: "1px solid var(--border)" }}>
              <div className="py-5 flex items-center justify-between cursor-pointer" onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)} data-testid={item.id}>
                <p className="text-sm font-medium pr-4" style={{ color: "var(--text-primary)" }}>{item.q}</p>
                {openFaq === item.id ? <ChevronUp size={16} style={{ color: "var(--text-tertiary)" }} /> : <ChevronDown size={16} style={{ color: "var(--text-tertiary)" }} />}
              </div>
              {openFaq === item.id && (
                <p className="pb-5 text-sm" style={{ color: "var(--text-secondary)", lineHeight: "1.7" }}>{item.a}</p>
              )}
            </div>
          ))}
          <div style={{ borderTop: "1px solid var(--border)" }} />
        </div>
      </div>

      <SiteFooter submissionId={submission?.id} />
    </div>
  );
}
